import { createSelector } from 'reselect'

import type { UserCallEvent } from '@/reducers/finances/userCallEventsReducer'
import type { ReduxState } from '@/utils/typeHelpers'
import {
  DATE_FORMATS_LUXON,
  formatISOLocal,
  isoToUTCDateTime,
} from '@/utils/dateHelpers'
import {
  LIVE_ONBOARDING_CALL_NAME,
  LIVE_ONBOARDING_SKIP_KEY_PREFIX,
} from './constants'

/** The booking to wait for: `eventUuid` when Calendly reported it, else any booking other than `previousEventUuid`. */
export interface AwaitedBooking {
  eventUuid: string | null
  previousEventUuid: string | null
}

const startMillis = (event: UserCallEvent): number =>
  event.startTime ? isoToUTCDateTime(event.startTime).toMillis() : 0

export const pickLiveOnboardingCallEvent = (
  events: UserCallEvent[]
): UserCallEvent | undefined =>
  events
    .filter(
      (event) =>
        event.callName === LIVE_ONBOARDING_CALL_NAME &&
        !event.canceledAt &&
        Boolean(event.startTime)
    )
    .reduce<
      UserCallEvent | undefined
    >((latest, event) => (!latest || startMillis(event) > startMillis(latest) ? event : latest), undefined)

const selectUserCallEvents = (state: ReduxState) => state.userCallEvents

export const selectLiveOnboardingCallEvent = createSelector(
  selectUserCallEvents,
  (events): UserCallEvent | undefined =>
    pickLiveOnboardingCallEvent(Object.values(events))
)

export const eventUuidFromUri = (uri: string): string | null =>
  uri.split('/').filter(Boolean).pop() ?? null

export const matchesAwaitedBooking = (
  event: UserCallEvent,
  awaited: AwaitedBooking
): boolean =>
  awaited.eventUuid
    ? event.calendlyEventId === awaited.eventUuid
    : event.calendlyEventId !== awaited.previousEventUuid

export const formatSessionTime = (startTime: string): string =>
  formatISOLocal(startTime, DATE_FORMATS_LUXON.WEEKDAY_SHORT_DATE_AT_TIME_TZ)

const skipKey = (userId: number): string =>
  `${LIVE_ONBOARDING_SKIP_KEY_PREFIX}:${userId}`

export const readSkipped = (userId: number): boolean => {
  try {
    return localStorage.getItem(skipKey(userId)) === 'true'
  } catch {
    return false
  }
}

export const writeSkipped = (userId: number): void => {
  try {
    localStorage.setItem(skipKey(userId), 'true')
  } catch {
    // Storage can be unavailable in private mode.
  }
}
